import React, { useEffect, useState, useRef } from 'react';
import { X, Image as ImageIcon, Send, User, Users, Search, Building } from 'lucide-react';
interface NewForumTopicProps {
  onClose: () => void;
  onSubmit: (topic: {
    title: string;
    category: string;
    topic: string;
    content: string;
    image: File | null;
    visibility: 'public' | 'institution' | 'private';
    selectedUsers: string[];
  }) => void;
  activeTab?: 'question' | 'other';
}
export const NewForumTopic: React.FC<NewForumTopicProps> = ({
  onClose,
  onSubmit,
  activeTab = 'question'
}) => {
  // Form state
  const [form, setForm] = useState({
    title: '',
    category: '',
    topic: '',
    content: '',
    image: null as File | null,
    visibility: 'public' as 'public' | 'institution' | 'private'
  });
  // UI state
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  // LGS subjects for dropdown
  const lgsSubjects = [{
    id: 'turkce',
    title: 'Türkçe',
    topics: ['Sözcükte Anlam', 'Deyimler ve Atasözleri', 'Cümlede Anlam', 'Sözel Mantık / Görsel Okuma', 'Parçada Anlam', 'Söz Sanatları', 'Metin Türleri', 'Noktalama İşaretleri', 'Yazım Kuralları', 'Fiilimsiler', 'Cümlenin Öğeleri', 'Cümle Türleri', 'Anlatım Bozukluğu']
  }, {
    id: 'matematik',
    title: 'Matematik',
    topics: ['Çarpanlar ve Katlar', 'Üslü Sayılar', 'Köklü Sayılar', 'Veri Analizi', 'Olasılık', 'Cebirsel İfadeler', 'Doğrusal Denklemler ve Eğim', 'Eşitsizlikler', 'Üçgenler', 'Eşlik ve Benzerlik', 'Dönüşüm Geometrisi', 'Geometrik Cisimler']
  }, {
    id: 'fen',
    title: 'Fen Bilimleri',
    topics: ['Mevsimler ve İklim', 'DNA ve Genetik Kod', 'Basınç', 'Madde ve Endüstri', 'Basit Makineler', 'Enerji Dönüşümleri', 'Elektrik Yükleri']
  }, {
    id: 'inkilap',
    title: 'T.C. İnkılap Tarihi',
    topics: ['Bir Kahraman Doğuyor', 'Milli Uyanış', 'Milli Bir Destan', 'Atatürkçülük', 'Demokratikleşme Çabaları', 'Atatürk Dönemi Dış Politika']
  }, {
    id: 'ingilizce',
    title: 'İngilizce',
    topics: ['Friendship', 'Teen Life', 'In The Kitchen', 'On The Phone', 'The Internet', 'Adventures', 'Tourism', 'Chores', 'Science', 'Natural Forces']
  }, {
    id: 'din',
    title: 'Din Kültürü',
    topics: ['Kader İnancı', 'Hz. Musa', 'Zekat ve Sadaka', 'Din ve Hayat', 'Hz. Muhammed', "Kur'an-ı Kerim", 'Paylaşma ve Yardımlaşma']
  }];
  // Sample teachers for private messages
  const availableTeachers = [{
    id: '1',
    name: 'Ahmet Yılmaz',
    subject: 'Matematik'
  }, {
    id: '2',
    name: 'Zeynep Kaya',
    subject: 'Türkçe'
  }, {
    id: '3',
    name: 'Mehmet Demir',
    subject: 'Fen Bilimleri'
  }, {
    id: '4',
    name: 'Ayşe Şahin',
    subject: 'İngilizce'
  }, {
    id: '5',
    name: 'Ali Öztürk',
    subject: 'Tarih'
  }];
  // Get available topics based on selected category
  const getAvailableTopics = () => {
    if (!form.category) return [];
    const subject = lgsSubjects.find(s => s.title === form.category);
    return subject ? subject.topics : [];
  };
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setForm({
      ...form,
      [name]: value
    });
  };
  // Handle image selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setForm({
        ...form,
        image: file
      });
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  // Clear image
  const clearImage = () => {
    setForm({
      ...form,
      image: null
    });
    setImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };
  // Select/deselect a teacher for private messages
  const toggleTeacherSelection = (teacherId: string) => {
    if (selectedTeachers.includes(teacherId)) {
      setSelectedTeachers(selectedTeachers.filter(id => id !== teacherId));
    } else {
      setSelectedTeachers([...selectedTeachers, teacherId]);
    }
  };
  // Filter teachers based on search term
  const filteredTeachers = availableTeachers.filter(teacher => teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) || teacher.subject.toLowerCase().includes(searchTerm.toLowerCase()));
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...form,
      selectedUsers: selectedTeachers
    });
  };
  return <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-1535">
      <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 w-full max-w-2xl" data-id="element-1536">
        <div className="bg-black rounded-lg p-6 max-h-[90vh] overflow-y-auto" data-id="element-1537">
          <div className="flex justify-between items-center mb-6" data-id="element-1538">
            <h2 className="text-xl font-semibold" data-id="element-1539">
              {activeTab === 'question' ? 'Yeni Soru Sor' : 'Yeni Konu Oluştur'}
            </h2>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-800" data-id="element-1540">
              <X className="w-5 h-5" data-id="element-1541" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1542">
            {/* Title */}
            <div data-id="element-1543">
              <label className="block text-sm text-gray-400 mb-1" data-id="element-1544">Başlık</label>
              <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600/40 to-teal-600/40" data-id="element-1545">
                <input type="text" name="title" value={form.title} onChange={handleChange} placeholder={activeTab === 'question' ? 'Sorunuzu kısaca açıklayan bir başlık girin' : 'Konuyu kısaca açıklayan bir başlık girin'} className="w-full bg-gray-900 rounded-lg py-2 px-3 focus:outline-none" required data-id="element-1546" />
              </div>
            </div>
            {/* Category and Topic (only for questions) */}
            {activeTab === 'question' && <>
                <div data-id="element-1547">
                  <label className="block text-sm text-gray-400 mb-1" data-id="element-1548">
                    Ders
                  </label>
                  <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600/40 to-teal-600/40" data-id="element-1549">
                    <select name="category" value={form.category} onChange={handleChange} className="w-full bg-gray-900 rounded-lg py-2 px-3 focus:outline-none appearance-none" required data-id="element-1550">
                      <option value="" disabled data-id="element-1551">
                        Ders seçin
                      </option>
                      {lgsSubjects.map(subject => <option key={subject.id} value={subject.title} data-id="element-1552">
                          {subject.title}
                        </option>)}
                    </select>
                  </div>
                </div>
                {form.category && <div data-id="element-1553">
                    <label className="block text-sm text-gray-400 mb-1" data-id="element-1554">
                      Konu
                    </label>
                    <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600/40 to-teal-600/40" data-id="element-1555">
                      <select name="topic" value={form.topic} onChange={handleChange} className="w-full bg-gray-900 rounded-lg py-2 px-3 focus:outline-none appearance-none" required data-id="element-1556">
                        <option value="" disabled data-id="element-1557">
                          Konu seçin
                        </option>
                        {getAvailableTopics().map(topic => <option key={topic} value={topic} data-id="element-1558">
                            {topic}
                          </option>)}
                      </select>
                    </div>
                  </div>}
              </>}
            {/* Content */}
            <div data-id="element-1559">
              <label className="block text-sm text-gray-400 mb-1" data-id="element-1560">İçerik</label>
              <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600/40 to-teal-600/40" data-id="element-1561">
                <textarea name="content" value={form.content} onChange={handleChange} placeholder={activeTab === 'question' ? 'Sorunuzu detaylı bir şekilde açıklayın...' : 'Konuyu detaylı bir şekilde açıklayın...'} rows={6} className="w-full bg-gray-900 rounded-lg py-2 px-3 focus:outline-none resize-none" required data-id="element-1562"></textarea>
              </div>
            </div>
            {/* Image upload */}
            <div data-id="element-1563">
              <label className="block text-sm text-gray-400 mb-1" data-id="element-1564">
                Görsel (İsteğe bağlı)
              </label>
              {!imagePreview ? <div className="border border-dashed border-gray-700 rounded-lg p-4 flex flex-col items-center justify-center" data-id="element-1565">
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" ref={imageInputRef} data-id="element-1566" />
                  <button type="button" onClick={() => imageInputRef.current?.click()} className="bg-gray-800 hover:bg-gray-700 text-gray-300 px-4 py-2 rounded-lg flex items-center transition-colors" data-id="element-1567">
                    <ImageIcon className="w-5 h-5 mr-2" data-id="element-1568" />
                    Görsel Seç
                  </button>
                  <p className="text-xs text-gray-500 mt-2" data-id="element-1569">
                    PNG, JPG veya GIF, max 5MB
                  </p>
                </div> : <div className="relative" data-id="element-1570">
                  <img src={imagePreview} alt="Önizleme" className="max-h-60 rounded-lg object-contain" data-id="element-1571" />
                  <button type="button" onClick={clearImage} className="absolute top-2 right-2 p-1 bg-black/70 rounded-full" data-id="element-1572">
                    <X className="w-4 h-4 text-red-400" data-id="element-1573" />
                  </button>
                </div>}
            </div>
            {/* Visibility */}
            <div data-id="element-1574">
              <label className="block text-sm text-gray-400 mb-1" data-id="element-1575">
                Görünürlük
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-1576">
                <button type="button" className={`flex flex-col items-center justify-center p-3 rounded-lg border ${form.visibility === 'public' ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400' : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600'}`} onClick={() => setForm({
                ...form,
                visibility: 'public'
              })} data-id="element-1577">
                  <User className="w-5 h-5 mb-1" data-id="element-1578" />
                  <span className="text-sm" data-id="element-1579">Herkese Açık</span>
                </button>
                <button type="button" className={`flex flex-col items-center justify-center p-3 rounded-lg border ${form.visibility === 'institution' ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400' : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600'}`} onClick={() => setForm({
                ...form,
                visibility: 'institution'
              })} data-id="element-1580">
                  <Building className="w-5 h-5 mb-1" data-id="element-1581" />
                  <span className="text-sm" data-id="element-1582">Kurum İçi</span>
                </button>
                <button type="button" className={`flex flex-col items-center justify-center p-3 rounded-lg border ${form.visibility === 'private' ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400' : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600'}`} onClick={() => {
                setForm({
                  ...form,
                  visibility: 'private'
                });
                setShowUserSearch(true);
              }} data-id="element-1583">
                  <Users className="w-5 h-5 mb-1" data-id="element-1584" />
                  <span className="text-sm" data-id="element-1585">Özel</span>
                </button>
              </div>
            </div>
            {/* User selection for private messages */}
            {form.visibility === 'private' && <div data-id="element-1586">
                <label className="block text-sm text-gray-400 mb-1" data-id="element-1587">
                  Öğretmenler
                </label>
                <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600/40 to-teal-600/40 mb-2" data-id="element-1588">
                  <div className="relative flex items-center" data-id="element-1589">
                    <Search className="absolute left-3 text-gray-500 w-4 h-4" data-id="element-1590" />
                    <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Öğretmen ara..." className="w-full bg-gray-900 rounded-lg py-2 pl-10 pr-4 focus:outline-none" data-id="element-1591" />
                  </div>
                </div>
                {selectedTeachers.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-1592">
                    {selectedTeachers.map(id => {
                const teacher = availableTeachers.find(t => t.id === id);
                return teacher ? <div key={teacher.id} className="flex items-center bg-emerald-600/20 text-emerald-400 px-2 py-1 rounded-full text-sm" data-id="element-1593">
                          {teacher.name}
                          <button type="button" onClick={() => toggleTeacherSelection(teacher.id)} className="ml-1 p-0.5 rounded-full hover:bg-emerald-500/20" data-id="element-1594">
                            <X className="w-3 h-3" data-id="element-1595" />
                          </button>
                        </div> : null;
              })}
                  </div>}
                <div className="bg-gray-900 rounded-lg max-h-40 overflow-y-auto" data-id="element-1596">
                  {filteredTeachers.length > 0 ? filteredTeachers.map(teacher => <div key={teacher.id} className={`flex items-center justify-between p-2 hover:bg-gray-800 cursor-pointer ${selectedTeachers.includes(teacher.id) ? 'bg-emerald-600/10' : ''}`} onClick={() => toggleTeacherSelection(teacher.id)} data-id="element-1597">
                        <div className="flex items-center" data-id="element-1598">
                          <div className="h-8 w-8 rounded-full bg-gray-800 flex items-center justify-center mr-2" data-id="element-1599">
                            {teacher.name.charAt(0)}
                          </div>
                          <div data-id="element-1600">
                            <p className="text-sm font-medium" data-id="element-1601">
                              {teacher.name}
                            </p>
                            <p className="text-xs text-gray-400" data-id="element-1602">
                              {teacher.subject}
                            </p>
                          </div>
                        </div>
                        <div className={`h-4 w-4 rounded-full border ${selectedTeachers.includes(teacher.id) ? 'bg-emerald-500 border-emerald-500' : 'border-gray-600'}`} data-id="element-1603">
                          {selectedTeachers.includes(teacher.id) && <div className="flex items-center justify-center h-full" data-id="element-1604">
                              <div className="h-2 w-2 rounded-full bg-white" data-id="element-1605"></div>
                            </div>}
                        </div>
                      </div>) : <div className="p-3 text-center text-gray-500" data-id="element-1606">
                      Sonuç bulunamadı
                    </div>}
                </div>
                {form.visibility === 'private' && selectedTeachers.length === 0 && <p className="text-xs text-amber-400 mt-2" data-id="element-1607">
                      Lütfen en az bir öğretmen seçin
                    </p>}
              </div>}
            {/* Submit button */}
            <div className="flex justify-end space-x-3 pt-3" data-id="element-1608">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors" data-id="element-1609">
                İptal
              </button>
              <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors flex items-center" disabled={form.visibility === 'private' && selectedTeachers.length === 0} data-id="element-1610">
                <Send className="w-4 h-4 mr-2" data-id="element-1611" />
                {activeTab === 'question' ? 'Soruyu Gönder' : 'Konuyu Oluştur'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
};